import { RSAA } from 'redux-api-middleware'
import {
  getApiPath,
  makeQueryParams,
  webStorage,
} from 'sidecar-utility-library'
import { getAuthedUser } from 'sidecar-utilities'

import { jiraCommentsFormatter } from 'utils/jira'
import { types, initialState } from 'store/MemberCommunication'
import { memberTestFlag } from './utils'

export const postMessage = requestPayload => {
  // Env based test flag which is added for all ticket created outside of
  // PROD. The purpose is that there is only 1 JIRA project for all envs
  requestPayload.mcomControl = memberTestFlag
  // The memberPhiUuidList is a list of all of the member UUIDs that the
  // logged-in user had access to. This list is used by the BE for validation
  requestPayload.memberPhiUuidList =
    JSON.parse(webStorage.getItem('accountMemberAccessMap'))?.PHI || []

  // Format the request data that will be passed in the body of the request
  const formData = new FormData()
  for (const [key, value] of Object.entries(requestPayload)) {
    if (key === 'files') {
      value.forEach(file => {
        formData.append('files', file, file.name)
      })
    } else {
      formData.append(`${key}`, value)
    }
  }

  return {
    [RSAA]: {
      endpoint: getApiPath(`comm`, `jira/member/create`),
      body: formData,
      headers: {
        'Content-Type': 'IGNORE',
      },
      method: 'POST',
      types: [
        types.POST_MESSAGE_REQUEST,
        types.POST_MESSAGE_SUCCESS,
        types.POST_MESSAGE_ERROR,
      ],
    },
  }
}

export const clearNewMessage = () => ({
  type: types.CLEAR_NEW_MESSAGE,
})

/**
 * Fetches an array of member communication requests based on the member uuid
 *
 * @param {array} memberUuids Required list of member uuids
 */
export const setActiveRequest = request => {
  return {
    type: types.SET_ACTIVE_MESSAGE,
    payload: request,
  }
}

export const postRequestComment = ({ ticketKey, comment, files }) => {
  const formData = new FormData()
  const member = getAuthedUser()

  if (files && files.length > 0) {
    files.forEach(file => {
      formData.append('files', file, file.name)
    })
  }
  if (member?.uuid) {
    formData.append('comment', `|-${member.uuid}-|: ${comment}`)
  } else if (comment) {
    formData.append('comment', comment)
  }

  // memberUuids is a list of all of the member UUIDs that the
  // logged-in user had access to. This list is used by the BE for validation
  formData.append(
    'memberUuids',
    JSON.parse(webStorage.getItem('accountMemberAccessMap'))?.PHI || [],
  )

  return {
    [RSAA]: {
      endpoint: getApiPath(`comm`, `jira/issue/${ticketKey}/comment`),
      body: formData,
      headers: {
        'Content-Type': 'IGNORE',
      },
      method: 'POST',
      types: [
        types.POST_REQUEST_COMMENT_REQUEST,
        types.POST_REQUEST_COMMENT_SUCCESS,
        types.POST_REQUEST_COMMENT_ERROR,
      ],
    },
  }
}

export const fetchSingleCommsMessage = ticketId => {
  // Future development could incorporate a PROD SUPPORT project
  const project = ['MEMBER']
  // Future development could incorporate a PROD SUPPORT ticket type
  const ticketType = ['Member Care Issue Tracking']
  const requestPayload = {
    project,
    ticketType,
    // this endpoint accepts a boolean for the test flag
    test: memberTestFlag === 'test',
    // The memberUuid is a list of all of the member UUIDs that the
    // logged-in user had access to. This list is used by the BE for validation
    memberUuid:
      JSON.parse(webStorage.getItem('accountMemberAccessMap'))?.PHI || [],
  }

  return {
    [RSAA]: {
      endpoint: getApiPath(
        `comm`,
        `jira/member/search/${ticketId}${makeQueryParams(requestPayload)}`,
      ),
      method: 'GET',
      types: [
        types.FETCH_SINGLE_COMMS_MESSAGE_REQUEST,
        types.FETCH_SINGLE_COMMS_MESSAGE_SUCCESS,
        types.FETCH_SINGLE_COMMS_MESSAGE_ERROR,
      ],
    },
  }
}

export const fetchMemberCommsMessages = () => {
  // Future development could incorporate a PROD SUPPORT project
  const project = ['MEMBER']
  // Future development could incorporate a PROD SUPPORT ticket type
  const ticketType = ['Member Care Issue Tracking']
  const requestPayload = {
    project,
    ticketType,
    // this endpoint accepts a boolean for the test flag
    test: memberTestFlag === 'test',
    // The memberUuid is a list of all of the member UUIDs that the
    // logged-in user had access to. This list is used by the BE for validation
    memberUuid:
      JSON.parse(webStorage.getItem('accountMemberAccessMap'))?.PHI || [],
  }

  return {
    [RSAA]: {
      endpoint: getApiPath(
        `comm`,
        `jira/member/search${makeQueryParams(requestPayload)}`,
      ),
      method: 'GET',
      types: [
        types.FETCH_MEMBER_COMMS_MESSAGES_REQUEST,
        types.FETCH_MEMBER_COMMS_MESSAGES_SUCCESS,
        types.FETCH_MEMBER_COMMS_MESSAGES_ERROR,
      ],
    },
  }
}

export const clearMemberCommsMessages = () => ({
  type: types.CLEAR_MEMBER_COMMS_MESSAGES,
})

/**
 * Mark the list as knowably behind reality: a ticket has just been created and
 * the search endpoint cannot see it yet.
 *
 * Dispatched by `SidekickProvider` when Ash hands off to the inbox, and cleared
 * by the re-fetch it schedules. Without it the inbox has no way to tell "I have
 * no messages loaded" from "I have none loaded _and_ one is on its way", so it
 * fetches on mount, gets a list that predates the new ticket, and renders it as
 * settled content — which the later re-fetch then visibly corrects.
 *
 * A flag on the list rather than something the inbox works out for itself: the
 * fact only exists out where the hand-off happened.
 */
export const awaitNewTicket = () => ({
  type: types.AWAIT_NEW_TICKET,
})

/**
 * How long to wait before asking for a list that includes a just-filed ticket.
 *
 * Two comments land on a new ticket after creation (one from the backend, one
 * from Jira automation) and the search endpoint will return the ticket without
 * them. `RequestForm` has waited this long after its own submit for the same
 * reason since long before Ash existed; this is that number, named.
 *
 * It lives here rather than in `SidekickProvider` because two entry points now
 * need it — the in-SPA hand-off and the one that arrives from member-app
 * through `applyPendingTicketWait` — and a second copy of a guess is a guess
 * that will drift.
 *
 * A guess about someone else's system either way. If it proves short, the fix
 * is a poll rather than a bigger number: the number can only ever be wrong.
 */
export const INBOX_INDEXING_DELAY_MS = 3200

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case types.POST_MESSAGE_REQUEST:
      return {
        ...state,
        newRequest: {
          busy: true,
          entity: {},
          error: null,
        },
      }
    case types.POST_MESSAGE_SUCCESS:
      return {
        ...state,
        newRequest: {
          entity: payload,
          busy: false,
          error: null,
        },
        // The attachment error occurs when a new message is successfully
        // posted to Jira, but the attachments fail. The endpoint returns a
        // successful response, but the attachment error is returned in the
        // payload.message. The error is displayed on the message detail page
        attachmentError: {
          error: payload?.message,
        },
      }
    case types.POST_MESSAGE_ERROR:
      return {
        ...state,
        newRequest: {
          entity: {},
          busy: false,
          error:
            payload?.response?.message || 'Your message failed to be saved',
        },
      }

    case types.CLEAR_NEW_MESSAGE:
      return {
        ...state,
        newRequest: {
          entity: {},
          busy: false,
          error: null,
        },
      }

    case types.SET_ACTIVE_MESSAGE:
      return {
        ...state,
        requests: {
          ...state.requests,
          activeRequest: payload,
        },
      }

    case types.POST_REQUEST_COMMENT_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          busy: true,
          error: null,
        },
        // Clear the attachment error if there is one. This error
        // occurs when a new message is submitted but the attachments fail
        // we'll clear the message and if the new attachments fail, we'll
        // re-populate the error (TODO: lights-1923)
        attachmentError: {
          error: null,
        },
      }
    case types.POST_REQUEST_COMMENT_SUCCESS:
      return {
        ...state,
      }
    case types.POST_REQUEST_COMMENT_ERROR:
      return {
        ...state,
        requests: {
          ...state.requests,
          busy: false,
          error: payload,
        },
      }

    case types.FETCH_SINGLE_COMMS_MESSAGE_REQUEST:
    case types.FETCH_MEMBER_COMMS_MESSAGES_REQUEST:
      return {
        ...state,
        requests: {
          ...state.requests,
          busy: true,
          error: null,
        },
      }

    case types.FETCH_SINGLE_COMMS_MESSAGE_SUCCESS: {
      const newRequests = [...state.requests.entities]
      const index = newRequests.findIndex(
        request => request.ticketId === payload.ticketId,
      )
      const formattedPayload = jiraCommentsFormatter(payload)
      if (index > -1) {
        newRequests[index] = formattedPayload
      }

      const newKeyMap = { ...state.requests.keyMap }
      newKeyMap[payload.ticketId] = formattedPayload

      const newExpenseMap = { ...state.requests.expenseMap }
      const expenseId = payload.expenseInfo?.id
      if (expenseId) {
        const expenseRequests = newExpenseMap[expenseId] || []
        // Replace the existing entry for this ticket rather than appending.
        // Appending caused the same message to render twice in the expense's
        // "Related communication" section until the next full fetch rebuilt
        // the map (BASH-367).
        const existingIndex = expenseRequests.findIndex(
          request => request.ticketId === payload.ticketId,
        )
        const updatedRequests = [...expenseRequests]

        if (existingIndex > -1) {
          updatedRequests[existingIndex] = formattedPayload
        } else {
          updatedRequests.push(formattedPayload)
        }

        newExpenseMap[expenseId] = updatedRequests
      }

      return {
        ...state,
        requests: {
          ...state.requests,
          busy: false,
          entities: newRequests,
          keyMap: newKeyMap,
          expenseMap: newExpenseMap,
        },
      }
    }

    case types.FETCH_SINGLE_COMMS_MESSAGE_ERROR:
      return {
        ...state,
        requests: {
          ...state.requests,
          busy: false,
          error: 'There was an error fetching the message',
        },
      }

    case types.FETCH_MEMBER_COMMS_MESSAGES_SUCCESS:
      if (payload.constructor == Array && payload.length > 0) {
        const requestsWithSortedComments = payload.map(request => {
          return jiraCommentsFormatter(request)
        })
        // A ticket with no comments yet sorts to the top, and cannot throw.
        //
        // `comments[0].created` was read unguarded, so a single comment-less
        // ticket in the payload crashed this reducer with a TypeError — no error
        // boundary catches a throw in a reducer, so the page went white. That is
        // reachable state, not a hypothetical: `jiraCommentsFormatter` returns the
        // request untouched when `comments` is empty, and the search endpoint
        // returns a just-created ticket seconds before its two comments (backend +
        // Jira automation) are indexed, which is the entire reason the 3.2s waits
        // in `RequestForm` and `SidekickProvider` exist.
        //
        // Those waits are a race, not a guard — if indexing ever takes longer than
        // the guess, they lose. This makes the reducer total instead, so the worst
        // case is a list ordered slightly oddly for a moment rather than a crash.
        //
        // Sorting the comment-less ticket first is deliberate: it is the newest
        // thing the member has, and it is why they are looking at this screen.
        const commentedAt = request =>
          request.comments?.[0]?.created
            ? new Date(request.comments[0].created).getTime()
            : Infinity
        const sortedRequests = requestsWithSortedComments.sort(
          (request1, request2) => commentedAt(request2) - commentedAt(request1),
        )
        const keyMap = sortedRequests.reduce(
          (obj, item) => Object.assign(obj, { [item.ticketId]: item }),
          {},
        )

        const expenseMap = {}
        sortedRequests.forEach(request => {
          if (request.expenseInfo?.id) {
            if (expenseMap[request.expenseInfo?.id]) {
              expenseMap[request.expenseInfo?.id] = [
                ...expenseMap[request.expenseInfo?.id],
                request,
              ]
            } else {
              expenseMap[request.expenseInfo?.id] = [request]
            }
          }
        })

        return {
          ...state,
          requests: {
            ...state.requests,
            busy: false,
            entities: sortedRequests,
            keyMap,
            expenseMap,
            // Whatever this fetch saw is now the truth we have. Cleared even if the
            // new ticket still isn't in it: the wait is over either way, and leaving
            // it set would hold the inbox on a skeleton forever.
            awaitingNewTicket: false,
          },
        }
      }

      return {
        ...state,
        requests: {
          ...state.requests,
          busy: false,
          awaitingNewTicket: false,
        },
      }

    case types.FETCH_MEMBER_COMMS_MESSAGES_ERROR:
      return {
        ...state,
        requests: {
          ...state.requests,
          busy: false,
          error: 'There was an error fetching the messages',
          // Cleared on the error path too, or a failed re-fetch leaves the inbox
          // waiting on a fetch that will never come back.
          awaitingNewTicket: false,
        },
      }

    case types.AWAIT_NEW_TICKET:
      return {
        ...state,
        requests: {
          ...state.requests,
          awaitingNewTicket: true,
          // A failure from before the ticket was filed is no longer the truth: we're
          // waiting on a fetch that will answer for itself. Left set, it renders the
          // error *and* the skeleton at once — `RequestError` sits outside the branch
          // the skeleton is in — which is a state no member should ever be shown.
          // `FETCH_..._REQUEST` clears it too, but not for the 3.2s until it fires.
          error: null,
        },
      }

    case types.CLEAR_MEMBER_COMMS_MESSAGES:
      return {
        ...state,
        requests: {
          ...state.requests,
          entities: [],
          busy: false,
          error: null,
          awaitingNewTicket: false,
        },
      }

    default:
      return state
  }
}
